import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useAuth } from '../utils/authContext';
import Layout from '../components/Layout';
import { Download, Search, Filter, AlertCircle, ChevronRight, ChevronDown, Package, Edit } from 'lucide-react';
import { CSVLink } from 'react-csv';
import InventoryManagement from '../components/InventoryManagement';

const GET_INVENTORY_REPORT = gql`
  query GetInventoryReport($catalogId: ID!, $filter: String) {
    inventoryReport(catalogId: $catalogId, filter: $filter) {
      items {
        id
        name
        category {
          id
          name
        }
        manageInventory
        stockTotal
        hasVariants
        priceVariants {
          variant
          price
          stock
          sku
          lowStockThreshold
        }
      }
      lowStockCount
      outOfStockCount
    }
  }
`;

const InventoryReport = () => {
    const { selectedCatalog } = useAuth();
    const [filter, setFilter] = useState('all'); // all, low, out
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredItems, setFilteredItems] = useState([]);
    const [expandedItems, setExpandedItems] = useState({});

    const [selectedItem, setSelectedItem] = useState(null);
    const [showInventoryManagement, setShowInventoryManagement] = useState(false);


    const { loading, error, data } = useQuery(GET_INVENTORY_REPORT, {
        variables: {
            catalogId: selectedCatalog?.id,
            filter
        },
        skip: !selectedCatalog?.id
    });

    useEffect(() => {
        if (data?.inventoryReport?.items) {
            setFilteredItems(data.inventoryReport.items.filter(item =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase())
            ));
        }
    }, [data, searchTerm]);

    const toggleItemExpansion = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const getCsvData = () => {
        const csvData = [];

        // Add header row
        csvData.push(['Item Name', 'Category', 'SKU', 'Variant', 'Stock', 'Low Stock Threshold', 'Inventory Tracking']);

        // Add item data
        filteredItems.forEach(item => {
            if (item.hasVariants) {
                item.priceVariants.forEach(variant => {
                    csvData.push([
                        item.name,
                        item.category?.name || 'Uncategorized',
                        variant.sku || '',
                        variant.variant || 'Default',
                        item.manageInventory ? (variant.stock || 0) : 'Not tracked',
                        item.manageInventory ? (variant.lowStockThreshold || 5) : 'Not tracked',
                        item.manageInventory ? 'Yes' : 'No'
                    ]);
                });
            } else {
                const firstVariant = item.priceVariants?.[0];
                csvData.push([
                    item.name,
                    item.category?.name || 'Uncategorized',
                    firstVariant?.sku || '',
                    firstVariant?.variant || 'Default',
                    item.manageInventory ? (firstVariant?.stock || 0) : 'Not tracked',
                    item.manageInventory ? (firstVariant?.lowStockThreshold || 5) : 'Not tracked',
                    item.manageInventory ? 'Yes' : 'No'
                ]);
            }
        });

        return csvData;
    };

    // Function to render the stock status badge
    const renderStockBadge = (stock, threshold = 5) => {
        if (stock <= 0) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    <AlertCircle size={12} className="mr-1" />
                    Out of Stock
                </span>
            );
        } else if (stock < threshold) {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                    Low: {stock}
                </span>
            );
        } else {
            return (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    In Stock: {stock}
                </span>
            );
        }
    };

    // Handler to open inventory management modal
    const handleManageInventory = (item) => {
        setSelectedItem(item);
        setShowInventoryManagement(true);
    };
    // Function to determine if the variant column should be shown
    const shouldShowVariantColumn = () => {
        if (!filteredItems || filteredItems.length === 0) return false;

        // Check if any item has variants other than just "Default"
        return filteredItems.some(item =>
            item.hasVariants && item.priceVariants.some(variant =>
                variant.variant && variant.variant !== 'default'
            )
        );
    };

    return (
        <Layout title="Inventory Report" description="View and manage your inventory items.">
            <div className="px-4 py-5 sm:px-6 mb-14">
                <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0 mb-4">
                    {/* <CSVLink
                        data={getCsvData()}
                        filename={`inventory-report-${new Date().toISOString().slice(0, 10)}.csv`}
                        className="inline-flex items-center justify-center px-4 py-2 bg-[#191970] text-white rounded text-sm font-medium hover:bg-opacity-90"
                    >
                        <Download size={16} className="mr-2" />
                        Export CSV
                    </CSVLink> */}
                </div>

                {data?.inventoryReport && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 mb-5">
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium mb-1 sm:text-base">Total Items</h3>
                            <p className="text-xl font-bold sm:text-2xl">{data.inventoryReport.items.length}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium mb-1 text-amber-600 sm:text-base">Low Stock Items</h3>
                            <p className="text-xl font-bold sm:text-2xl">{data.inventoryReport.lowStockCount}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium mb-1 text-red-600 sm:text-base">Out of Stock Items</h3>
                            <p className="text-xl font-bold sm:text-2xl">{data.inventoryReport.outOfStockCount}</p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0 mb-4">
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white pl-10 pr-4 py-2 border rounded-lg text-sm"
                            aria-label="Search inventory items"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    </div>

                    <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto justify-start">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 py-1.5 rounded text-sm whitespace-nowrap ${filter === 'all' ? 'bg-[#191970] text-white' : 'bg-gray-100'}`}
                        >
                            All Items
                        </button>
                        <button
                            onClick={() => setFilter('low')}
                            className={`px-3 py-1.5 rounded text-sm whitespace-nowrap ${filter === 'low' ? 'bg-amber-500 text-white' : 'bg-gray-100'}`}
                        >
                            Low Stock
                        </button>
                        <button
                            onClick={() => setFilter('out')}
                            className={`px-3 py-1.5 rounded text-sm whitespace-nowrap ${filter === 'out' ? 'bg-red-500 text-white' : 'bg-gray-100'}`}
                        >
                            Out of Stock
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#191970]"></div>
                        <span className="ml-3">Loading inventory data...</span>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 text-red-700 p-4 rounded text-sm">
                        Error loading inventory: {error.message}
                    </div>
                ) : (
                    <>
                        {/* Mobile view - Card-based layout with management buttons */}
                        <div className="block sm:hidden">
                            {filteredItems.length === 0 ? (
                                <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                                    No items found matching your criteria
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredItems.map(item => (
                                        <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden">
                                            <div className="p-4">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex-1">
                                                        <h3 className="font-medium">{item.name}</h3>
                                                        <p className="text-sm text-gray-500">{item.category?.name || 'Uncategorized'}</p>
                                                    </div>

                                                    {/* Add manage button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleManageInventory(item);
                                                        }}
                                                        className="p-1.5 bg-[#191970] bg-opacity-10 rounded-full text-[#191970]"
                                                        aria-label="Manage inventory"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                </div>

                                                <div
                                                    className="flex justify-between items-center cursor-pointer"
                                                    onClick={() => toggleItemExpansion(item.id)}
                                                >
                                                    <div className="flex items-center">
                                                        {item.manageInventory ? (
                                                            item.hasVariants ? (
                                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                                                    {item.priceVariants?.length || 0} variants
                                                                </span>
                                                            ) : (
                                                                renderStockBadge(item.priceVariants?.[0]?.stock || 0, item.priceVariants?.[0]?.lowStockThreshold)
                                                            )
                                                        ) : (
                                                            <span className="text-xs text-gray-400">Not tracked</span>
                                                        )}
                                                    </div>

                                                    {item.hasVariants && (
                                                        <div className="flex items-center text-sm text-gray-500">
                                                            {expandedItems[item.id] ? (
                                                                <>Hide details <ChevronDown size={16} /></>
                                                            ) : (
                                                                <>Show details <ChevronRight size={16} /></>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Variant details for expandable items */}
                                                {item.hasVariants && expandedItems[item.id] && (
                                                    <div className="mt-3 border-t border-gray-100 pt-3">
                                                        {item.priceVariants.map((variant, idx) => (
                                                            <div key={idx} className="py-2 flex justify-between items-center">
                                                                <div>
                                                                    <p className="font-medium text-sm">{variant.variant || 'Default'}</p>
                                                                    {variant.sku && <p className="text-xs text-gray-500">SKU: {variant.sku}</p>}
                                                                </div>
                                                                <div>{renderStockBadge(variant.stock, variant.lowStockThreshold)}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Desktop view - Table layout with management buttons */}
                        <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Item
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Category
                                            </th>
                                            {shouldShowVariantColumn() && (
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Variant
                                                </th>
                                            )}
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                SKU
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Stock
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredItems.map(item => (
                                            item.hasVariants ? (
                                                // Display variants individually
                                                item.priceVariants.map((variant, vIdx) => (
                                                    <tr key={`${item.id}-${vIdx}`}>
                                                        {vIdx === 0 && (
                                                            <td rowSpan={item.priceVariants.length} className="px-6 py-4 align-top">
                                                                {item.name}
                                                            </td>
                                                        )}
                                                        {vIdx === 0 && (
                                                            <td rowSpan={item.priceVariants.length} className="px-6 py-4 align-top">
                                                                {item.category?.name || 'Uncategorized'}
                                                            </td>
                                                        )}
                                                        {shouldShowVariantColumn() && (
                                                            <td className="px-6 py-4">
                                                                {variant.variant || 'Default'}
                                                            </td>
                                                        )}
                                                        <td className="px-6 py-4">
                                                            {variant.sku || '-'}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {renderStockBadge(variant.stock, variant.lowStockThreshold)}
                                                        </td>
                                                        {vIdx === 0 && (
                                                            <td rowSpan={item.priceVariants.length} className="px-6 py-4 align-top">
                                                                <button
                                                                    onClick={() => handleManageInventory(item)}
                                                                    className="text-[#191970] bg-gray-50 hover:text-blue-700 flex items-center"
                                                                >
                                                                    <Edit size={16} className="mr-1" /> Manage
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                // Display single item (non-variant)
                                                <tr key={item.id}>
                                                    <td className="px-6 py-4">
                                                        {item.name}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {item.category?.name || 'Uncategorized'}
                                                    </td>
                                                    {shouldShowVariantColumn() && (
                                                        <td className="px-6 py-4">
                                                            Default
                                                        </td>
                                                    )}
                                                    <td className="px-6 py-4">
                                                        -
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {item.manageInventory ? (
                                                            renderStockBadge(item.priceVariants?.[0]?.stock || 0, item.priceVariants?.[0]?.lowStockThreshold)
                                                        ) : (
                                                            <span className="text-gray-400 italic">Not tracked</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => handleManageInventory(item)}
                                                            className="text-[#191970] bg-gray-50 hover:text-blue-700 flex items-center"
                                                        >
                                                            <Edit size={16} className="mr-1" /> Manage
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        ))}

                                        {filteredItems.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                                                    No items found matching your criteria
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Inventory Management Modal */}
            {showInventoryManagement && selectedItem && (
                <InventoryManagement
                    item={selectedItem}
                    onClose={() => {
                        setShowInventoryManagement(false);
                        setSelectedItem(null);
                    }}
                    catalogId={selectedCatalog?.id}
                />
            )}
        </Layout>
    );
};

export default InventoryReport;